import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useFormValidation } from '../../hooks/useFormValidation';
import apiClient from '../../services/api';
import FormButton from '../../components/common/stories/FormButton';

/**
 * ProfileForm - Personal profile information only
 * Organization/company setup happens after token validation
 */
const ProfileForm = ({ onProfileSubmit }) => {
  const { user } = useAuth0();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: user.email,
    phone: '',
    country: '',
    terms_accepted: false,
  });

  // Define validation rules
  const validationRules = {
    firstname: (value) => (!value ? 'First Name is required' : ''),
    lastname: (value) => (!value ? 'Last Name is required' : ''),
    email: (value) => {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return !value || !emailPattern.test(value) ? 'A valid Email is required' : '';
    },
    phone: (value) => {
      const phonePattern = /^\d{3}-\d{3}-\d{4}$/;
      return !value || !phonePattern.test(value)
        ? 'Phone Number must be in the format xxx-xxx-xxxx'
        : '';
    },
    country: (value) => (!value ? 'Country is required' : ''),
    terms_accepted: (value) => (!value ? 'You must accept the Terms & Conditions' : ''),
  };

  // Use the form validation hook
  const { errors, validateField, validateFields } = useFormValidation();

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData({
      ...formData,
      [name]: newValue,
    });

    // Validate the field as user types
    validateField(name, newValue, validationRules[name]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields before submission
    const isValid = validateFields(
      Object.keys(formData).reduce((acc, key) => {
        acc[key] = {
          value: formData[key],
          validation: validationRules[key],
        };
        return acc;
      }, {})
    );

    if (!isValid) return;

    setIsSubmitting(true);
    const userId = decodeURIComponent(user.sub);

    try {
      // Update user profile in backend using apiClient
      const response = await apiClient.patch(`/api/users/${userId}`, {
        ...formData,
        user_id: userId,
      });

      // Handle different response structures
      const result = response.data?.result || response.data;
      if (result) {
        onProfileSubmit(result.country || formData.country);
      } else {
        // If no result, use the form data directly
        onProfileSubmit(formData.country);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      // Show user-friendly error
      alert('Failed to save profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        .profile-form-container {
          width: 100%;
          max-width: 600px;
          margin: 0 auto;
          padding: 0 20px;
          box-sizing: border-box;
        }

        .profile-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px 30px;
        }

        @media (max-width: 768px) {
          .profile-form-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
          .profile-form-container {
            padding: 0 15px;
          }
        }

        .profile-field {
          display: flex;
          flex-direction: column;
        }

        .profile-field.full-width {
          grid-column: 1 / -1;
        }

        .profile-field label {
          display: block;
          margin-bottom: 8px;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
        }

        .profile-field input,
        .profile-field select {
          width: 100%;
          padding: 12px 14px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          line-height: 1.5;
          color: #111827;
          background-color: #fff;
          box-sizing: border-box;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .profile-field input:focus,
        .profile-field select:focus {
          outline: none;
          border-color: #ff8264;
          box-shadow: 0 0 0 3px rgba(255, 130, 100, 0.15);
        }

        .profile-field input:read-only {
          background-color: #f9fafb;
          color: #6b7280;
        }

        .profile-field select {
          appearance: none;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
          background-position: right 12px center;
          background-repeat: no-repeat;
          background-size: 16px;
          padding-right: 40px;
        }

        .profile-field .error-message {
          color: #dc2626;
          font-size: 12px;
          margin-top: 6px;
        }

        .checkbox-field {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          grid-column: 1 / -1;
        }

        .checkbox-field input[type="checkbox"] {
          width: 18px;
          height: 18px;
          margin-top: 2px;
          accent-color: #ff8264;
          cursor: pointer;
        }

        .checkbox-field label {
          margin-bottom: 0;
          cursor: pointer;
          font-size: 14px;
          color: #374151;
        }

        .submit-row {
          grid-column: 1 / -1;
          margin-top: 8px;
        }
      `}</style>

      <div className="profile-form-container">
        <form onSubmit={handleSubmit}>
          <div className="profile-form-grid">
            {/* First Name */}
            <div className="profile-field">
              <label htmlFor="firstname">First Name</label>
              <input
                type="text"
                id="firstname"
                name="firstname"
                value={formData.firstname}
                onChange={handleInputChange}
                placeholder="Enter your first name"
                disabled={isSubmitting}
              />
              {errors.firstname && <span className="error-message">{errors.firstname}</span>}
            </div>

            {/* Last Name */}
            <div className="profile-field">
              <label htmlFor="lastname">Last Name</label>
              <input
                type="text"
                id="lastname"
                name="lastname"
                value={formData.lastname}
                onChange={handleInputChange}
                placeholder="Enter your last name"
                disabled={isSubmitting}
              />
              {errors.lastname && <span className="error-message">{errors.lastname}</span>}
            </div>

            {/* Email */}
            <div className="profile-field">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                readOnly
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            {/* Phone Number */}
            <div className="profile-field">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="xxx-xxx-xxxx"
                disabled={isSubmitting}
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>

            {/* Country of Operation */}
            <div className="profile-field full-width">
              <label htmlFor="country">Country</label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                disabled={isSubmitting}
              >
                <option value="">Select your country</option>
                <option value="Canada">Canada</option>
                <option value="USA">USA</option>
              </select>
              {errors.country && <span className="error-message">{errors.country}</span>}
            </div>

            {/* Terms Checkbox */}
            <div className="checkbox-field">
              <input
                type="checkbox"
                id="terms_accepted"
                name="terms_accepted"
                checked={formData.terms_accepted}
                onChange={handleInputChange}
                disabled={isSubmitting}
              />
              <label htmlFor="terms_accepted">
                I agree to Terms & Conditions and Privacy Policy
              </label>
            </div>
            {errors.terms_accepted && (
              <div className="profile-field full-width">
                <span className="error-message">{errors.terms_accepted}</span>
              </div>
            )}

            {/* Submit Button */}
            <div className="submit-row">
              <FormButton className="Button" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Continue'}
              </FormButton>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default ProfileForm;
